<template>
  <div>
    <div class="breadcrumb--container">
      <nav-breadcrumbs
        blankLabel="All Bill Pay Categories"
        hideBrowserEventName="hide-st-browser"
        triggerTooltip="Select Service"
        toggleBrowserEventName="toggle-st-browser"
        :crumbs="serviceTypeCrumbs"
        :onSelect="serviceTypeSelected"
        class="my-2" />

      <div class="breadcrumb--right">
        <label class="align-content-center bill-pay-label">
          Bill Payments in:&nbsp;
        </label>
        <nav-breadcrumbs
            blankLabel="Change Location"
            hideBrowserEventName="hide-geo-browser"
            triggerTooltip="Change Location"
            toggleBrowserEventName="toggle-geo-browser"
            :crumbs="geoCrumbs"
            :onSelect="geoSelected"
            class="my-2" />
      </div>

    </div>

    <transition appear name="slide-y-transition">
      <v-card v-if="showStBrowser" class="st-overlay geo-page-nav__overlay elevation-1" :style="{top: stBrowserTop}">
        <service-type-browser :handleClose="handleClose" :onSelect="serviceTypeSelected" />
      </v-card>
    </transition>

    <transition appear name="slide-y-transition">
      <v-card v-if="showGeoBrowser" class="geo-overlay geo-page-nav__overlay elevation-1" :style="{top: geoBrowserTop}">
        <div class="v-subheader geo-page-nav__header">
          See Bill Payments in these locations:
          <v-layout justify-end>
            <v-btn icon large @click.stop="showGeoBrowser = false" style="color: #999;">
              <v-icon large>close</v-icon>
            </v-btn>
          </v-layout>
        </div>
        <geo-browser :nearbyGeos="nearbyGeos" :onSelect="geoSelected">
          <geo-searchbox :onSelect="geoSelected"/>
        </geo-browser>
      </v-card>
    </transition>
  </div>
</template>

<script>
  import ServiceTypeBrowser from './ServiceTypeBrowser'
  import GeoBrowser from '@/components/common/GeoBrowser.vue'
  import GeoSearchbox from '@/components/common/GeoSearchbox'
  import NavBreadcrumbs from './NavBreadcrumbs'
  import { apiPath } from './utils'
  import { eventBus } from '@/utils'
  import axios from 'axios'
  import { mapState, mapActions } from 'vuex'
  import airbrake from '@/helpers/airbrake'

  /**
   * @returns {boolean} true if parent is an ancestor of node, false otherwise
   */
  function isParent (node, parent) {
    if (!node || !parent) return false

    if (node.parentElement === parent) {
      return true
    } else {
      return isParent(node.parentElement, parent)
    }
  }

  export default {
    components: {
      GeoBrowser,
      GeoSearchbox,
      NavBreadcrumbs,
      ServiceTypeBrowser
    },

    mounted: function () {
      this.initOverlaysDisplay()
      eventBus('geo')
        .$on('update-st-browser', this.updateServiceTypeBrowser)

      document.addEventListener('keydown', e => {
        if (e.keyCode === 27) { // escape key maps to keycode `27`
          this.hideOverlays()
        }
      })
    },

    computed: {
      ...mapState('geo', ['serviceType', 'serviceTypeGroup']),
      ...mapState('heatmap', ['selectedGeo']),

      serviceTypeCrumbs () {
        const activeStg = this.serviceTypeGroup
        const activeSt = this.serviceType
        const stCrumbs = []
        if (activeStg || activeSt) {
          stCrumbs.push({ name: 'All Bill Pay Categories', slug: '' })
        }
        if (activeStg) {
          stCrumbs.push(activeStg)
        }
        if (activeSt) {
          stCrumbs.push({
            name: activeSt.name,
            slug: activeStg ? `${activeStg.slug}/${activeSt.name}` : activeSt.name
          })
        }
        return stCrumbs
      },

      geoAndSt () {
        return {
          geoSlug: this.selectedGeo ? this.selectedGeo.slug : undefined,
          stgSlug: this.serviceTypeGroup ? this.serviceTypeGroup.slug : undefined,
          stSlug: this.serviceType ? this.serviceType.slug : undefined
        }
      }
    },

    data: function () {
      const state = this.$store.state.geo
      return {
        showStBrowser: false,
        showGeoBrowser: false,
        stBrowserTop: '-1000px',
        geoBrowserTop: '-1000px',
        geoCrumbs: state.geoBrowser.bread_crumbs,
        nearbyGeos: state.geoBrowser.nearby
      }
    },

    methods: {
      ...mapActions('geo', ['setServiceTypeAndGroup']),

      geoSelected (geo) {
        this.showGeoBrowser = false
        eventBus('geo').$emit('hide-geo-browser')
        this.updateRoute(geo)
      },

      serviceTypeSelected (selectedServiceTypeGroup, selectedServiceType) {
        this.showStBrowser = false
        eventBus('geo').$emit('hide-st-browser')

        let stg, st
        if (selectedServiceTypeGroup && selectedServiceTypeGroup.slug !== '') { // STG other than 'All Services'
          stg = {
            name: selectedServiceTypeGroup.name,
            slug: selectedServiceTypeGroup.slug
          }
        }
        if (selectedServiceType) {
          st = {
            name: selectedServiceType.name,
            slug: selectedServiceType.slug
          }
        }
        this.setServiceTypeAndGroup({stg, st})
        this.updateRoute(this.selectedGeo)
      },

      updateRoute (geo) {
        this.$router.push({
          name: 'geo-show',
          params: {
            geoId: geo.slug,
            stg: this.serviceTypeGroup ? this.serviceTypeGroup.slug : null,
            st: this.serviceType ? this.serviceType.slug : null
          }
        })
      },

      hideOverlays () {
        this.showStBrowser = false
        this.showGeoBrowser = false
        eventBus('geo').$emit('hide-geo-browser')
        eventBus('geo').$emit('hide-st-browser')
      },

      updateGeoBrowser () {
        axios.get(`/g_api/geo_browser/${this.selectedGeo.slug}`)
          .then(response => this.$store.dispatch('geo/updateGeoBrowser', response.data))
          .catch(err => { airbrake.notify(err) })
      },

      updateServiceTypeBrowser () {
        axios.get(apiPath(this.$route, `/g_api/st_browser`))
          .then(response => this.$store.dispatch('geo/updateStBrowser', response.data))
          .catch(err => { airbrake.notify(err) })
      },

      initOverlaysDisplay: function () {
        const vm = this
        document.addEventListener('mousedown', e => {
          function hideOnClickOutside (showBrowser, overlaySelector, hideEventName) {
            if (vm[showBrowser] && !isParent(e.target, vm.$el.querySelector(overlaySelector) /* must be queried every time since it keeps changing */)) {
              vm[showBrowser] = false
              eventBus('geo').$emit(hideEventName)
            }
          }

          // if the click happened outside the browsers they must be closed
          hideOnClickOutside('showGeoBrowser', '.geo-overlay', 'hide-geo-browser')
          hideOnClickOutside('showStBrowser', '.st-overlay', 'hide-st-browser')
        })

        function registerToggleHandler (toggleEventName, showBrowser, showOtherBrowser, hideOtherBrowserEventName, browserTop) {
          eventBus('geo').$on(toggleEventName, (overlayTriggerBottom) => {
            vm[showBrowser] = !vm[showBrowser]
            if (vm[showBrowser]) {
              vm[showOtherBrowser] = false
              eventBus('geo').$emit(hideOtherBrowserEventName)
              vm[browserTop] = (overlayTriggerBottom - vm.$el.getBoundingClientRect().top) + 'px'
            }
          })
        }

        registerToggleHandler('toggle-geo-browser', 'showGeoBrowser', 'showStBrowser', 'hide-st-browser', 'geoBrowserTop')
        registerToggleHandler('toggle-st-browser', 'showStBrowser', 'showGeoBrowser', 'hide-geo-browser', 'stBrowserTop')
      },

      handleClose () {
        this.showStBrowser = false
      }
    },

    watch: {
      geoAndSt (current, previous) {
        if (current.geoSlug != previous.geoSlug) {
          this.updateGeoBrowser()
        }
        this.updateServiceTypeBrowser()
      }
    }
  }
</script>

<style lang="stylus">

  .breadcrumb--container
    display: flex
    justify-content: space-between
    flex-direction: row

  .breadcrumb--right
    display: flex
    align-items: center
    font-size: 13px

  @media (max-width: 599px)
    .breadcrumb--container
      justify-content: flex-start
      flex-direction: column

    .breadcrumb--right
      display: flex
      flex-direction: column
      align-items: flex-start

    .breadcrumb--right > label
      padding: 0 8px

  .geo-page-nav
    position: relative

    &__header
      font-weight: 500
      padding-left: 16px

    @media (min-width: 960px)
      &__header
        font-size: 24px

    &__overlay
      position: absolute
      z-index: 990
      border: 1px solid #d3d3d3
      background: white
      padding: 15px
      left: 0
      right: 0
      margin-top: 3px

</style>



// WEBPACK FOOTER //
// frontend/src/pages/Geo/components/PageNavigation.vue